import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/SimpleToast";
import { Shield, QrCode, Key, Loader2, CheckCircle, LogOut } from "lucide-react";

const API_URL = "/api";

export default function Setup2FA() {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(1); // 1: intro, 2: QR, 3: verify
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Session prüfen; bereits verifizierte Benutzer dürfen hier nicht erneut
    // einrichten (würde die aktive 2FA zurücksetzen) → zum Dashboard leiten.
    axios.get(`${API_URL}/auth/me`)
      .then((res) => { if (res.data?.totp_verified) navigate("/"); })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const handleStartSetup = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/auth/totp/setup`);
      setSetupData(response.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Einrichten der 2FA");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/totp/verify`, { code });
      toast.success("2FA erfolgreich aktiviert!");
      // Redirect to dashboard - user already has valid token
      setTimeout(() => {
        window.location.href = "/";
      }, 800);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ungültiger Code");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (e) {
      // ignore
    }
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900">
      <div className="w-full max-w-md animate-fade-in" data-testid="setup-2fa-page">
        <div className="text-center mb-8">
          <img
            src="/assets/rnetwork-logo.webp"
            alt="R-Network GmbH"
            className="h-16 mx-auto mb-4 bg-white rounded-lg px-4 py-2"
          />
          <h1 className="text-3xl font-bold text-white">2FA einrichten</h1>
          <p className="text-slate-400 mt-2">Erforderlich für alle Benutzer</p>
        </div>

        <Card className="border-border/50 shadow-2xl">
          {step === 1 && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <CardTitle>2-Faktor-Authentifizierung Pflicht</CardTitle>
                <CardDescription>
                  Aus Sicherheitsgründen muss jeder Benutzer 2FA einrichten, bevor er das CRM nutzen kann.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-2">
                  <p className="font-medium">Was du brauchst:</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>Google Authenticator App (oder Authy, Microsoft Authenticator)</li>
                    <li>Smartphone mit Kamera (für QR-Code-Scan)</li>
                    <li>Ca. 2 Minuten Zeit</li>
                  </ol>
                </div>
                <Button
                  onClick={handleStartSetup}
                  disabled={submitting}
                  className="w-full h-11"
                  data-testid="start-setup-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Jetzt einrichten
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="w-full"
                  data-testid="setup-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </Button>
              </CardContent>
            </>
          )}

          {step === 2 && setupData && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-primary" />
                  QR-Code scannen
                </CardTitle>
                <CardDescription>
                  Öffne Google Authenticator und scanne diesen Code
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center">
                  <div className="p-4 bg-white rounded-lg">
                    <img
                      src={setupData.qr_code}
                      alt="2FA QR Code"
                      className="w-48 h-48"
                      data-testid="setup-qr-code"
                    />
                  </div>
                </div>
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Oder manuell eingeben:
                  </p>
                  <code className="block p-2 bg-muted rounded text-sm font-mono break-all">
                    {setupData.secret}
                  </code>
                </div>
                <Button onClick={() => setStep(3)} className="w-full h-11" data-testid="setup-next-btn">
                  Weiter zum Code
                </Button>
              </CardContent>
            </>
          )}

          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-primary" />
                  Code bestätigen
                </CardTitle>
                <CardDescription>
                  Gib den 6-stelligen Code aus deiner Authenticator App ein
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center py-2">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-code">Authentifizierungscode</Label>
                  <Input
                    id="setup-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    autoFocus
                    data-testid="setup-verify-input"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Code läuft alle 30 Sekunden ab
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1"
                    data-testid="setup-back-btn"
                  >
                    Zurück
                  </Button>
                  <Button
                    onClick={handleVerify}
                    disabled={submitting || code.length !== 6}
                    className="flex-1"
                    data-testid="setup-verify-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aktivieren"}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          © 2026 R-Network GmbH - Nur für internen Gebrauch
        </p>
      </div>
    </div>
  );
}
